/**
 * Product Sales Date Filter Component
 *
 * Component for filtering product sales report by date range
 */

import React, {useState, useCallback, useMemo, useEffect} from 'react';
import {ProductSalesReportFilters} from '../models/productSalesReport.model';
import DatePickerButton from '../../../Presentation/components/ui/DatePickerButton';
import Button from '../../../Presentation/components/ui/Button';
import {AppCardTitle, AppText} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';

interface ProductSalesDateFilterProps {
  filters: ProductSalesReportFilters;
  onFiltersChange: (filters: ProductSalesReportFilters) => void;
}

export const ProductSalesDateFilter: React.FC<ProductSalesDateFilterProps> = ({
  filters,
  onFiltersChange
}) => {
  const [localFilters, setLocalFilters] = useState<ProductSalesReportFilters>(() => ({
    date_from: filters.date_from,
    date_to: filters.date_to
  }));

  // Sync local filters when filters change
  useEffect(() => {
    setLocalFilters({
      date_from: filters.date_from,
      date_to: filters.date_to
    });
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = useCallback((key: 'date_from' | 'date_to', value: string | undefined) => {
    setLocalFilters(prev => ({...prev, [key]: value}));
  }, []);

  // Apply filters
  const applyFilters = useCallback(() => {
    onFiltersChange(localFilters);
  }, [localFilters, onFiltersChange]);

  // Reset filters
  const resetFilters = useCallback(() => {
    setLocalFilters({});
    onFiltersChange({});
  }, [onFiltersChange]);

  // Check if filters have changed
  const hasChanges = useMemo(() => {
    return localFilters.date_from !== filters.date_from || localFilters.date_to !== filters.date_to;
  }, [localFilters, filters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(localFilters.date_from || localFilters.date_to);
  }, [localFilters]);

  return (
    <Card className="p-6">
      <div className="space-y-4">
        {/* Header */}
        <div>
          <AppCardTitle className="text-gray-900 mb-2">
            فلاتر التاريخ
          </AppCardTitle>
          <AppText className="text-gray-600">
            اختر الفترة الزمنية لعرض البيانات المطلوبة
          </AppText>
        </div>

        {/* Filters Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <DatePickerButton
            label="تاريخ البداية"
            value={localFilters.date_from}
            onChange={(date) => handleFilterChange('date_from', date)}
            placeholder="اختر تاريخ البداية"
            className="w-full"
            maxDate={localFilters.date_to}
          />
          <DatePickerButton
            label="تاريخ النهاية"
            value={localFilters.date_to}
            onChange={(date) => handleFilterChange('date_to', date)}
            placeholder="اختر تاريخ النهاية"
            className="w-full"
            minDate={localFilters.date_from}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-3 space-x-reverse">
            <Button
              onClick={applyFilters}
              variant="primary"
              size="medium"
              disabled={!hasChanges}
              className="px-6"
            >
              تطبيق الفلاتر
            </Button>
            <Button
              onClick={resetFilters}
              variant="outline"
              size="medium"
              disabled={!hasActiveFilters}
              className="px-6"
            >
              إعادة تعيين
            </Button>
          </div>

          {/* Active Filters Count */}
          {hasActiveFilters && (
            <div className="flex items-center space-x-2 space-x-reverse">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">
                {[localFilters.date_from, localFilters.date_to].filter(Boolean).length} فلتر نشط
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

